import { Controller } from "@hotwired/stimulus"
import { Decimal } from "decimal.js"

// Connects to data-controller="app--session-participant-activity-form-row"
export default class extends Controller {

	static outlets = ['app--session-activity']
	static targets = ['cellColumn', 'cell'] // cellColumn is the td element, cell is the contenteditable div
	static values = {
		participantId: String,
		newRecord: { type: Boolean, default: false },
	}

	connect() {
		this.updateColumns(this.sessionActivityModels(), true)
		this.baseInputName = 'session[session_participant_activities_attributes][]'
	}

	updateColumns(session_activities) {
		if (!this.hasCellColumnTarget) { console.error('missing required cellColumnTarget'); return }

		const oldCellColumns = this.cellColumnTargets
		const firstCellColumn = this.cellColumnTargets[0]

		session_activities.forEach(sa => {
			const saHours                 = new Decimal((sa['duration'] || 0)).div(60)
			const existingCellColumn      = this.getExistingCellColumn(sa['activity_id'], oldCellColumns)
			const existingCell            = existingCellColumn?.querySelector('[contenteditable]')
			const cellDuration            = this.getCellDuration(existingCell, saHours)
			const existingInputAttributes = this.getInputParameters(existingCellColumn)
			const spaId     = existingInputAttributes?.['session[session_participant_activities_attributes][][id]']
			const sessionId = existingInputAttributes?.['session[session_participant_activities_attributes][][session_id]']

			const inputAttributes = {}
			if (spaId) { inputAttributes['session[session_participant_activities_attributes][][id]'] = spaId }
			if (sessionId) { inputAttributes['session[session_participant_activities_attributes][][session_id]'] = sessionId }
			inputAttributes['session[session_participant_activities_attributes][][participant_id]']  = this.participantIdValue
			inputAttributes['session[session_participant_activities_attributes][][activity_id]']     = sa['activity_id']
			inputAttributes['session[session_participant_activities_attributes][][duration_format]'] = 'hours'

			const cellElement = document.createElement('div')
			cellElement.innerHTML = this.decimalToHoursFormat(cellDuration)
			cellElement.contentEditable = 'true'
			cellElement.classList.add('px-2', 'py-1', 'flex-grow-1', 'ms-2')
			cellElement.style = 'font-size: 20px; text-align: right; min-width: 100px;'
			cellElement.dataset.contenteditableInitialValue = this.decimalToHoursFormat(saHours)
			cellElement.dataset.spreadsheetTarget = 'cell'
			cellElement.setAttribute('data-app--session-participant-activity-form-row-target', 'cell')
			cellElement.dataset.originalValue  = existingCell?.dataset?.originalValue || ''
			cellElement.dataset.sessionActivityHours = this.decimalToHoursFormat(saHours)
			if (this.newRecordValue) { cellElement.dataset.markedForCreation = '1' }
			cellElement.dataset.inputName = 'session[session_participant_activities_attributes][][duration]'
			cellElement.dataset.inputAttributes = JSON.stringify(inputAttributes)
			cellElement.dataset.dirtyElementTarget = 'element'
			cellElement.dataset.action = 'input->spreadsheet#updateAddRemoveButtons input->dirty-element#updateDirtyness'

			const columnElement = document.createElement('td')
			columnElement.classList.add('border', 'py-1')
			columnElement.setAttribute('data-app--session-participant-activity-form-row-target', 'cellColumn') // setAttr... needed due to double dash identifier

			let deleteButtonElement = document.createElement('button')
			deleteButtonElement.type = 'button'
			deleteButtonElement.classList.add('delete-value', 'text-600', 'border-0', 'bg-white', 'rounded', 'hover-bg-200', 'px-2', 'py-2')
			deleteButtonElement.dataset.action = "spreadsheet#deleteValue"
			deleteButtonElement.tabIndex = '-1'
			deleteButtonElement.innerHTML = '<div class="fa fal fa-times fa-fw me-0"></div>'

			let setButtonElement = document.createElement('button')
			setButtonElement.type = 'button'
			setButtonElement.classList.add('set-initial-value', 'text-primary', 'border-0', 'bg-white', 'rounded', 'hover-bg-200', 'px-2', 'py-2')
			setButtonElement.dataset.action = "spreadsheet#setInitialValue"
			setButtonElement.tabIndex = '-1'
			setButtonElement.innerHTML = '<div class="fa fal fa-check fa-fw me-0"></div>'

			if (cellDuration === null || cellDuration === '') {
				deleteButtonElement.classList.add('d-none')
			} else {
				setButtonElement.classList.add('d-none')
			}

			const flexElement = document.createElement('div')
			flexElement.classList.add('hstack', 'justify-content-between')

			flexElement.append(deleteButtonElement)
			flexElement.append(setButtonElement)
			flexElement.append(cellElement)

			columnElement.append(flexElement)

			firstCellColumn.before(columnElement)
		})

		oldCellColumns.forEach(occ => occ.remove())
	}

	delete(_event) {
		let rowIsPersisted = this.cellTargets.some(cell => {
			const cellInputParameters = this.getInputParameters(cell)
			const cellId = cellInputParameters[`${this.baseInputName}[id]`]
			return cellId ? true : false
		})

		if (rowIsPersisted) {
			this.cellTargets.forEach(cell => {
				this.setInputParameter(cell, `${this.baseInputName}[_destroy]`, '1')
				cell.dataset.markedForDestruction = '1'
				this.sendInputEvent(cell)
			})
			this.element.classList.add('d-none')
		} else {
			this.element.remove()
		}

	}

	undelete(_event) {
		this.cellTargets.forEach(cell => {
			this.setInputParameter(cell, `${this.baseInputName}[_destroy]`, '0')
			cell.removeAttribute('data-marked-for-destruction')
			this.sendInputEvent(cell)
		})
		this.element.classList.remove('d-none')
	}

	setInputParameter(cell, name, value) {
		const inputAttributes = this.getInputParameters(cell)
		inputAttributes[name] = value
		cell.dataset.inputAttributes = JSON.stringify(inputAttributes)
	}

	getInputParameters(cellColumn) {
		if (!cellColumn) { return null }

		let inputElement = null
		if (cellColumn.dataset.inputAttributes) { inputElement = cellColumn }
		else { inputElement = cellColumn.querySelector('[data-input-attributes]') }

		if (!inputElement) { return {} }
		return JSON.parse(inputElement.dataset.inputAttributes)
	}

	sendInputEvent(element) {
		const event = new Event('input', { bubbles: true })
		element.dispatchEvent(event)
	}

	// pull sessionActivity models from SessionActivityController
	sessionActivityModels() {
		return this.appSessionActivityOutlet.sessionActivityModels()
	}

	// update sessionActivity models from outlet calls
	updateSessionActivities(session_activities) {
		this.updateColumns(session_activities)
	}

	getExistingCellColumn(activity_id, cellColumns) {
		return cellColumns.find(occ => {
			const occ_ia = this.getInputParameters(occ)
			if (occ_ia && occ_ia['session[session_participant_activities_attributes][][activity_id]'] == activity_id) { return occ }
		})
	}

	// get the duration, but only if it was modified from the previous session_activity duration.
	getModifiedDuration(cellColumn) {
		const cellColumnInput = cellColumn?.querySelector('[contenteditable]')
		if (cellColumnInput && this.isCellModified(cellColumnInput) && cellColumnInput.innerText.trim() != '') {
			return cellColumnInput.innerText.trim()
		} else {
			return null
		}
	}

	isCellModified(cell) {
		if (!cell) { return false }
		if (cell.dataset.sessionActivityHours === cell.innerText.trim()) { return false }
		return true
	}

	getCellDuration(existingCell, sessionActivityHours) {
		const existingDuration = existingCell?.innerText.trim()

		let cellDuration = null
		if (this.newRecordValue) {
			if (this.isCellModified(existingCell)) {
				cellDuration = new Decimal(existingDuration)
			} else {
				cellDuration = sessionActivityHours
			}
		} else {
			if (!existingDuration && this.isCellModified(existingCell)) {
				cellDuration = new Decimal(0)
			} else if (existingDuration && this.isCellModified(existingCell)) {
				cellDuration = new Decimal(existingDuration)
			} else {
				cellDuration = sessionActivityHours
			}
		}
		cellDuration ||= new Decimal(0)
		return cellDuration
	}

	decimalToHoursFormat(decimal) {
		let formattedString = ''

		if (decimal.greaterThan(0)) {
			if (decimal.isInt()) {
				formattedString = decimal.toString()
			} else {
				if (decimal.mod(1.0).eq(0.5)) {
					formattedString = decimal.toFixed(1)
				} else {
					formattedString = decimal.toFixed(2)
				}
			}
		} 

		return formattedString
	}

}
